import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Download, Sparkles, RefreshCw, CheckCircle2, AlertTriangle, ArrowUpRight, PackageSearch, ChevronDown, ChevronUp, Pencil, ImagePlus, Eraser } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_STYLES = {
  "Pending": "bg-slate-100 text-slate-600",
  "Needs Review": "bg-amber-50 text-amber-600",
  "Generating": "bg-indigo-50 text-indigo-600",
  "Generated": "bg-emerald-50 text-emerald-600",
  "Edited": "bg-blue-50 text-blue-600",
  "Approved": "bg-emerald-100 text-emerald-700",
  "Failed": "bg-red-50 text-red-600",
};

const JOB_STATUS_STYLES = {
  "Draft": "bg-slate-100 text-slate-600",
  "Generating": "bg-indigo-50 text-indigo-600",
  "Completed": "bg-emerald-50 text-emerald-600",
  "Partial": "bg-amber-50 text-amber-600",
  "Failed": "bg-red-50 text-red-600",
};

const MISSING_LABELS = {
  product: "batch.miss.product",
  template: "batch.miss.template",
  brand: "batch.miss.brand",
  logo: "batch.miss.logo",
  upc: "batch.miss.upc",
  productImage: "batch.miss.productImage",
  productNameEN: "batch.miss.productNameEN",
  standardCard: "batch.miss.standardCard",
};

const FONT_FAMILY_OPTIONS = [
  { value: "none", label: "batch.font.none" },
  { value: "sans-serif", label: "batch.font.sansSerif" },
  { value: "serif", label: "batch.font.serif" },
  { value: "monospace", label: "batch.font.monospace" },
  { value: "condensed-sans-serif", label: "batch.font.condensed" },
];

export default function BatchColorCards() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const stLabel = (x) => t("batch.status." + x, x);
  const [contractNumber, setContractNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [overrideItem, setOverrideItem] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [brands, setBrands] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ templateId: "", brandId: "", upc: "", customerLogo: "", standardCardCode: "" });
  const [savingOverride, setSavingOverride] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobList, setJobList] = useState([]);
  const [listVisible, setListVisible] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [settingsJob, setSettingsJob] = useState(null);
  const [settingsForm, setSettingsForm] = useState({ customerLogoOverride: "", titleFontSize: "", bodyFontSize: "", titleFontFamily: "none", bodyFontFamily: "none" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogoBg, setRemovingLogoBg] = useState(false);
  const [regenerateAllPrompt, setRegenerateAllPrompt] = useState(null);
  const pollRef = useRef(null);

  const loadJobList = async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/batch/jobs");
      setJobList(data);
    } catch (e) {
      // silently ignore - list is a convenience panel
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadJobList();
    const savedId = sessionStorage.getItem("bhf_batch_job");
    if (savedId) restoreJob(savedId);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (job?.id) sessionStorage.setItem("bhf_batch_job", job.id);
  }, [job?.id]);

  const restoreJob = async (jobId) => {
    try {
      const { data } = await api.get(`/batch/jobs/${jobId}`);
      setJob(data);
      setContractNumber(data.contractNumber);
      if (data.status === "Generating") { setGenerating(true); pollJob(data.id); }
    } catch (e) {
      sessionStorage.removeItem("bhf_batch_job");
    }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollJob = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/batch/jobs/${jobId}`);
        setJob(data);
        if (data.status !== "Generating") {
          stopPolling();
          setGenerating(false);
        }
      } catch (e) {
        stopPolling();
        setGenerating(false);
      }
    }, 1800);
  };

  const handleLoad = async () => {
    const num = contractNumber.trim();
    if (!num) { toast.error(t("batch.enterContract")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/batch/load-order", { contractNumber: num });
      setJob(data);
      if (data.status === "Generating") { setGenerating(true); pollJob(data.id); }
      toast.success(t("batch.loaded", { n: data.contractNumber, c: data.itemCount }));
      loadJobList();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  const openContract = async (item) => {
    try {
      const { data } = await api.get(`/batch/jobs/${item.id}`);
      setJob(data);
      setContractNumber(data.contractNumber);
      if (data.status === "Generating") { setGenerating(true); pollJob(data.id); }
    } catch (e) {
      toast.error(t("batch.loadFailed"));
    }
  };

  const openSettings = (item) => {
    setSettingsJob(item);
    setSettingsForm({
      customerLogoOverride: item.customerLogoOverride || "",
      titleFontSize: item.titleFontSize || "",
      bodyFontSize: item.bodyFontSize || "",
      titleFontFamily: item.titleFontFamily || "none",
      bodyFontFamily: item.bodyFontFamily || "none",
    });
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettingsForm((f) => ({ ...f, customerLogoOverride: data.url }));
    } catch (e) {
      toast.error(t("batch.logoUploadFailed"));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogoBg = async () => {
    if (!settingsForm.customerLogoOverride) return;
    setRemovingLogoBg(true);
    try {
      const { data } = await api.post("/utils/remove-bg", { url: settingsForm.customerLogoOverride });
      setSettingsForm((f) => ({ ...f, customerLogoOverride: data.url }));
      toast.success(t("batch.whiteBgRemoved"));
    } catch (e) {
      toast.error(t("batch.removeBgFailed"));
    } finally {
      setRemovingLogoBg(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsJob) return;
    setSavingSettings(true);
    try {
      const payload = {
        customerLogoOverride: settingsForm.customerLogoOverride || null,
        titleFontSize: settingsForm.titleFontSize ? Number(settingsForm.titleFontSize) : null,
        bodyFontSize: settingsForm.bodyFontSize ? Number(settingsForm.bodyFontSize) : null,
        titleFontFamily: settingsForm.titleFontFamily === "none" ? null : settingsForm.titleFontFamily,
        bodyFontFamily: settingsForm.bodyFontFamily === "none" ? null : settingsForm.bodyFontFamily,
      };
      const { data } = await api.patch(`/batch/jobs/${settingsJob.id}/settings`, payload);
      if (job?.id === settingsJob.id) setJob(data);
      loadJobList();
      const hasGenerated = (data.items || []).some((it) => ["Generated", "Edited", "Approved"].includes(it.status));
      setSettingsJob(null);
      if (hasGenerated) {
        setRegenerateAllPrompt(data);
      } else {
        toast.success(t("batch.settingsSaved"));
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleGenerate = async () => {
    if (!job) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/batch/jobs/${job.id}/generate`);
      setJob(data);
      pollJob(job.id);
      toast.success(t("batch.genStarted"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setGenerating(false);
    }
  };

  const handleRegenerateAll = async () => {
    if (!regenerateAllPrompt) return;
    const jobId = regenerateAllPrompt.id;
    setRegenerateAllPrompt(null);
    setGenerating(true);
    try {
      const { data } = await api.post(`/batch/jobs/${jobId}/regenerate-all`);
      if (job?.id === jobId) setJob(data);
      pollJob(jobId);
      toast.success(t("batch.regenStarted"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setGenerating(false);
    }
  };

  const handleRegenerate = async (itemId) => {
    try {
      const { data } = await api.post(`/batch/jobs/${job.id}/items/${itemId}/regenerate`);
      setJob(data);
      toast.success(t("batch.regenerated"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const handleApprove = async (itemId) => {
    try {
      const { data } = await api.post(`/batch/jobs/${job.id}/items/${itemId}/approve`);
      setJob(data);
      toast.success(t("batch.approved"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const handleApproveAll = async () => {
    try {
      const { data } = await api.post(`/batch/jobs/${job.id}/approve-all`);
      setJob(data);
      toast.success(t("batch.allApproved"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const handleDownloadZip = async () => {
    if (!job) return;
    setDownloading(true);
    try {
      const res = await api.get(`/batch/jobs/${job.id}/download-zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${job.contractNumber}_ColorCards.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("batch.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const openOverride = async (item) => {
    setOverrideItem(item);
    setOverrideForm({
      templateId: item.templateId || "",
      brandId: item.brandId || "",
      upc: item.upc || "",
      customerLogo: item.customerLogo || "",
      standardCardCode: item.standardCardCode || "",
    });
    if (templates.length === 0) {
      try { const { data } = await api.get("/templates"); setTemplates(data); } catch (e) {}
    }
    if (brands.length === 0) {
      try { const { data } = await api.get("/brands"); setBrands(data); } catch (e) {}
    }
  };

  const handleSaveOverride = async () => {
    if (!overrideItem) return;
    setSavingOverride(true);
    try {
      const payload = { standardCardCode: overrideForm.standardCardCode || null };
      if (overrideForm.templateId) payload.templateId = overrideForm.templateId;
      if (overrideForm.brandId) payload.brandId = overrideForm.brandId;
      if (overrideForm.upc) payload.upc = overrideForm.upc;
      if (overrideForm.customerLogo) payload.customerLogo = overrideForm.customerLogo;
      const { data } = await api.post(`/batch/jobs/${job.id}/items/${overrideItem.itemId}/override`, payload);
      setJob(data);
      toast.success(t("batch.infoUpdated"));
      setOverrideItem(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSavingOverride(false);
    }
  };

  const items = job?.items || [];
  const progressPct = job?.itemCount ? Math.round((job.completedCount / job.itemCount) * 100) : 0;
  const canApproveAll = items.some((it) => ["Generated", "Edited"].includes(it.status) && !(it.missing || []).length);
  const canDownload = items.some((it) => it.colorCardDesignId);

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="batch-page">
      <div className="mb-7">
        <h1 className="font-display text-2xl font-700 text-slate-900">{t("batch.title")}</h1>
        <p className="mt-1.5 text-sm text-slate-500">{t("batch.subtitle")}</p>
      </div>

      <div className="bhf-card flex flex-col gap-3 p-5 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9"
            placeholder={t("batch.contractPlaceholder")}
            value={contractNumber}
            onChange={(e) => setContractNumber(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLoad()}
            data-testid="batch-contract-input"
          />
        </div>
        <Button className="bhf-btn-primary border-0 text-white" onClick={handleLoad} disabled={loading} data-testid="batch-load-button">
          <PackageSearch className="mr-1.5 h-4 w-4" /> {loading ? t("batch.loading") : t("batch.loadOrder")}
        </Button>
      </div>

      <div className="mt-6 bhf-card overflow-hidden" data-testid="batch-contract-list-section">
        <button
          type="button"
          onClick={() => setListVisible((v) => !v)}
          className="flex w-full items-center justify-between px-5 py-3.5 text-left"
          data-testid="batch-contract-list-toggle"
        >
          <span className="text-sm font-semibold text-slate-800">{t("batch.contractList")} {jobList.length > 0 && <span className="text-slate-400">({jobList.length})</span>}</span>
          {listVisible ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </button>
        {listVisible && (
          <div className="border-t border-slate-100">
            <Table data-testid="batch-contract-list-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>{t("batch.thContract")}</TableHead>
                  <TableHead>{t("batch.thCustomer")}</TableHead>
                  <TableHead>{t("batch.thDate")}</TableHead>
                  <TableHead>{t("batch.thStatus")}</TableHead>
                  <TableHead className="text-right">{t("batch.thActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobList.map((j) => (
                  <TableRow key={j.id} data-testid={`batch-contract-list-row-${j.id}`}>
                    <TableCell>
                      <button
                        type="button"
                        className="font-mono-bhf text-xs font-medium text-indigo-600 hover:underline"
                        onClick={() => openContract(j)}
                        data-testid={`batch-contract-list-open-${j.id}`}
                      >
                        {j.contractNumber}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{j.customer || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{j.createdAt ? new Date(j.createdAt).toLocaleDateString("zh-CN") : "—"}</TableCell>
                    <TableCell><Badge className={JOB_STATUS_STYLES[j.status] || "bg-slate-100 text-slate-600"}>{stLabel(j.status)}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openSettings(j)} data-testid={`batch-contract-edit-${j.id}`}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> {t("batch.edit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {jobList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-6 text-center text-sm text-slate-400">
                      {listLoading ? t("batch.loading") : t("batch.noContracts")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {job && (
        <div className="mt-6" data-testid="batch-job-panel">
          <div className="bhf-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono-bhf text-sm font-semibold text-indigo-600">{job.contractNumber}</span>
                <Badge className={JOB_STATUS_STYLES[job.status] || "bg-slate-100 text-slate-600"} data-testid="batch-job-status">{stLabel(job.status)}</Badge>
              </div>
              <div className="mt-1 text-sm text-slate-500">{job.customer || "—"} · {t("batch.itemsCount", { n: job.itemCount })}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button className="bhf-btn-primary border-0 text-white" onClick={handleGenerate} disabled={generating} data-testid="batch-generate-button">
                <Sparkles className="mr-1.5 h-4 w-4" /> {generating ? t("batch.generating") : t("batch.genAll")}
              </Button>
              <Button variant="outline" onClick={handleApproveAll} disabled={!canApproveAll} data-testid="batch-approve-all-button">
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("batch.approveAll")}
              </Button>
              <Button variant="outline" onClick={handleDownloadZip} disabled={!canDownload || downloading} data-testid="batch-download-zip-button">
                <Download className="mr-1.5 h-4 w-4" /> {downloading ? t("batch.packing") : t("batch.downloadZip")}
              </Button>
            </div>
          </div>

          {(generating || job.status === "Generating") && (
            <div className="mt-4 flex items-center gap-3" data-testid="batch-progress-bar">
              <Progress value={progressPct} className="flex-1" />
              <span className="text-xs font-medium text-slate-500 whitespace-nowrap">{t("batch.completed", { done: job.completedCount, total: job.itemCount })}</span>
            </div>
          )}

          <div className="mt-6 overflow-hidden rounded-xl border border-slate-200">
            <Table data-testid="batch-items-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>SKU</TableHead>
                  <TableHead>{t("batch.thProduct")}</TableHead>
                  <TableHead>{t("batch.thCategory")}</TableHead>
                  <TableHead>{t("batch.thStdCode")}</TableHead>
                  <TableHead>{t("batch.thStatus")}</TableHead>
                  <TableHead>{t("batch.thMissing")}</TableHead>
                  <TableHead className="text-right">{t("batch.thActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.itemId} data-testid={`batch-item-row-${item.itemId}`}>
                    <TableCell className="font-mono-bhf text-xs">{item.sku || "—"}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-sm">{item.productNameEN || item.productNameCN || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{item.category || "—"}</TableCell>
                    <TableCell>
                      {item.standardCardCode ? (
                        <span className="font-mono-bhf text-[11px] font-medium text-emerald-600">{item.standardCardCode}</span>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-slate-400 hover:text-indigo-600 hover:underline"
                          onClick={() => openOverride(item)}
                          data-testid={`batch-set-standard-code-${item.itemId}`}
                        >
                          {t("batch.linkStd")}
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[item.status] || "bg-slate-100 text-slate-600"} data-testid={`batch-item-status-${item.itemId}`}>{stLabel(item.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      {(item.missing || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {item.missing.map((m) => (
                            <span key={m} className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                              <AlertTriangle className="h-2.5 w-2.5" /> {t(MISSING_LABELS[m] || m)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {(item.missing || []).length > 0 && (
                          <Button size="sm" variant="outline" onClick={() => openOverride(item)} data-testid={`batch-fix-button-${item.itemId}`}>{t("batch.fixInfo")}</Button>
                        )}
                        {["Generated", "Edited", "Failed"].includes(item.status) && !(item.missing || []).length && (
                          <Button size="sm" variant="outline" onClick={() => handleRegenerate(item.itemId)} data-testid={`batch-regenerate-button-${item.itemId}`}>
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {item.colorCardDesignId && (
                          <Button size="sm" variant="outline" onClick={() => navigate(`/designer/${item.colorCardDesignId}`)} data-testid={`batch-edit-button-${item.itemId}`}>
                            <ArrowUpRight className="mr-1 h-3.5 w-3.5" /> {t("batch.edit")}
                          </Button>
                        )}
                        {["Generated", "Edited"].includes(item.status) && (
                          <Button size="sm" className="bhf-btn-primary border-0 text-white" onClick={() => handleApprove(item.itemId)} data-testid={`batch-approve-button-${item.itemId}`}>{t("batch.approve")}</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-400">{t("batch.noItems")}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={!!overrideItem} onOpenChange={(open) => !open && setOverrideItem(null)}>
        <DialogContent data-testid="batch-override-modal">
          <DialogHeader>
            <DialogTitle>{t("batch.fixTitle", { sku: overrideItem?.sku || "" })}</DialogTitle>
            <DialogDescription>{t("batch.fixDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">{t("batch.stdCodeLabel")}</Label>
              <Input value={overrideForm.standardCardCode} onChange={(e) => setOverrideForm((f) => ({ ...f, standardCardCode: e.target.value }))} placeholder={t("batch.stdCodePlaceholder")} data-testid="batch-override-standard-code-input" />
            </div>
            {overrideItem?.missing?.includes("template") && (
              <div>
                <Label className="text-xs text-slate-500">{t("batch.templateLabel")}</Label>
                <Select value={overrideForm.templateId} onValueChange={(v) => setOverrideForm((f) => ({ ...f, templateId: v }))}>
                  <SelectTrigger data-testid="batch-override-template-select"><SelectValue placeholder={t("batch.selectTemplate")} /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name || t.templateNumber}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {overrideItem?.missing?.includes("brand") && (
              <div>
                <Label className="text-xs text-slate-500">{t("batch.brandLabel")}</Label>
                <Select value={overrideForm.brandId} onValueChange={(v) => setOverrideForm((f) => ({ ...f, brandId: v }))}>
                  <SelectTrigger data-testid="batch-override-brand-select"><SelectValue placeholder={t("batch.selectBrand")} /></SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {overrideItem?.missing?.includes("upc") && (
              <div>
                <Label className="text-xs text-slate-500">{t("batch.upcLabel")}</Label>
                <Input value={overrideForm.upc} onChange={(e) => setOverrideForm((f) => ({ ...f, upc: e.target.value }))} placeholder={t("batch.upcPlaceholder")} data-testid="batch-override-upc-input" />
              </div>
            )}
            {overrideItem?.missing?.includes("logo") && (
              <div>
                <Label className="text-xs text-slate-500">{t("batch.logoUrlLabel")}</Label>
                <Input value={overrideForm.customerLogo} onChange={(e) => setOverrideForm((f) => ({ ...f, customerLogo: e.target.value }))} placeholder={t("batch.logoUrlPlaceholder")} data-testid="batch-override-logo-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideItem(null)} data-testid="batch-override-cancel-button">{t("batch.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleSaveOverride} disabled={savingOverride} data-testid="batch-override-save-button">
              {savingOverride ? t("batch.saving") : t("batch.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!settingsJob} onOpenChange={(open) => !open && setSettingsJob(null)}>
        <DialogContent data-testid="batch-settings-modal">
          <DialogHeader>
            <DialogTitle>{t("batch.settingsTitle", { n: settingsJob?.contractNumber || "" })}</DialogTitle>
            <DialogDescription>{t("batch.settingsDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">{t("batch.custLogoLabel")}</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {settingsForm.customerLogoOverride && (
                  <img src={fileUrl(settingsForm.customerLogoOverride)} alt="logo" className="h-12 w-12 rounded-md border border-slate-200 object-contain bg-white" data-testid="batch-settings-logo-preview" />
                )}
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <ImagePlus className="h-3.5 w-3.5" /> {uploadingLogo ? t("batch.uploading") : t("batch.uploadLogo")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadLogo(e.target.files?.[0])} data-testid="batch-settings-logo-input" />
                </label>
                {settingsForm.customerLogoOverride && (
                  <button type="button" onClick={handleRemoveLogoBg} disabled={removingLogoBg} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60" data-testid="batch-settings-logo-removebg">
                    <Eraser className="h-3.5 w-3.5" /> {removingLogoBg ? t("batch.processing") : t("batch.removeWhiteBg")}
                  </button>
                )}
              </div>
              <div className="mt-1 text-[10px] text-slate-400">{t("batch.logoNote")}</div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("batch.titleFontSize")}</Label>
              <Input
                type="number"
                min="1"
                value={settingsForm.titleFontSize}
                onChange={(e) => setSettingsForm((f) => ({ ...f, titleFontSize: e.target.value }))}
                placeholder={t("batch.egTitle")}
                data-testid="batch-settings-title-fontsize-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("batch.titleFontType")}</Label>
              <Select value={settingsForm.titleFontFamily} onValueChange={(v) => setSettingsForm((f) => ({ ...f, titleFontFamily: v }))}>
                <SelectTrigger data-testid="batch-settings-title-fontfamily-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_FAMILY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{t(o.label)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("batch.bodyFontSize")}</Label>
              <Input
                type="number"
                min="1"
                value={settingsForm.bodyFontSize}
                onChange={(e) => setSettingsForm((f) => ({ ...f, bodyFontSize: e.target.value }))}
                placeholder={t("batch.egBody")}
                data-testid="batch-settings-body-fontsize-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("batch.bodyFontType")}</Label>
              <Select value={settingsForm.bodyFontFamily} onValueChange={(v) => setSettingsForm((f) => ({ ...f, bodyFontFamily: v }))}>
                <SelectTrigger data-testid="batch-settings-body-fontfamily-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_FAMILY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{t(o.label)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsJob(null)} data-testid="batch-settings-cancel-button">{t("batch.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleSaveSettings} disabled={savingSettings} data-testid="batch-settings-save-button">
              {savingSettings ? t("batch.saving") : t("batch.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!regenerateAllPrompt} onOpenChange={(open) => !open && setRegenerateAllPrompt(null)}>
        <AlertDialogContent data-testid="batch-regenerate-all-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("batch.regenTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("batch.regenDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-regenerate-all-cancel">{t("batch.regenNo")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerateAll} data-testid="batch-regenerate-all-confirm">{t("batch.regenYes")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
